/**
 * Locale registry + locale-aware formatters — the single home of the Persian-digit
 * conversion that ~6 screens currently duplicate (faDigits / toLocaleString('fa-IR')).
 *
 * Mirrors the vanilla-Node template at packages/site/scripts/lib/i18n.mjs (LOCALES with
 * dir, a digit helper, locale-aware num) but for React/TS and fa+en only (no Arabic).
 *
 * Screen adoption of these formatters is a LATER task — this file only PROVIDES the shared
 * version; it does not touch the screens that still carry their own copies.
 */

export const LOCALES = [
  { code: 'fa', dir: 'rtl', label: 'فارسی' },
  { code: 'en', dir: 'ltr', label: 'English' },
] as const;

export type Locale = 'fa' | 'en';

/** Direction for a locale, from the LOCALES registry. Defaults to rtl (fa). */
export function dirFor(locale: Locale): 'rtl' | 'ltr' {
  return LOCALES.find((l) => l.code === locale)?.dir ?? 'rtl';
}

const FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

/**
 * Convert the Latin digits inside an already-formatted string to the locale's digits.
 * For 'en' the string is returned unchanged; for 'fa' 0-9 → ۰-۹.
 * Consolidates the `s.replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])` copies.
 */
export function formatDigits(locale: Locale, s: string): string {
  if (locale === 'en') return s;
  return s.replace(/[0-9]/g, (d) => FA_DIGITS[Number(d)]!);
}

/**
 * Locale-aware number formatting. 'fa' uses fa-IR (Persian digits + grouping),
 * 'en' uses en-US (Latin digits). `opts` are standard Intl.NumberFormatOptions.
 */
export function formatNumber(locale: Locale, n: number, opts?: Intl.NumberFormatOptions): string {
  return n.toLocaleString(locale === 'fa' ? 'fa-IR' : 'en-US', opts);
}

/**
 * Locale-aware date formatting from an ISO string. 'fa' renders the Persian (Jalali)
 * calendar via fa-IR; 'en' uses en-US. Empty/invalid input → em dash.
 */
export function formatDate(locale: Locale, iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale === 'fa' ? 'fa-IR' : 'en-US', { dateStyle: 'medium' });
}
